import { AppUserRecord, AdminStats } from '../types';
import { toggleTheme, isDarkMode } from '../utils/theme';
import { showToast } from '../utils/toast';

export class AdminPanel {
  private container: HTMLElement;
  private adminKey: string = '';
  private stats: AdminStats | null = null;
  private users: AppUserRecord[] = [];
  private searchQuery: string = '';
  private statusFilter: 'all' | 'active' | 'blocked' = 'all';
  private sortBy: 'active' | 'login' | 'handovers' | 'name' | 'id' = 'active';
  private revealedPasswords: Set<string> = new Set();
  private autoRefreshTimer: number | null = null;
  private isLoading: boolean = false;
  private currentPage: number = 1;
  private pageSize: number = 10;

  constructor(container: HTMLElement) {
    this.container = container;
    const savedKey = sessionStorage.getItem('vyko_admin_key') || '';
    if (savedKey) {
      this.verifySavedKeyAndInit(savedKey);
    } else {
      this.renderPasscodeGate();
    }
  }

  private async verifySavedKeyAndInit(savedKey: string) {
    try {
      const res = await fetch('/api/admin/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: savedKey }),
      });

      if (res.ok) {
        this.adminKey = savedKey;
        this.render();
        this.fetchData();
        this.startAutoRefresh();
      } else {
        sessionStorage.removeItem('vyko_admin_key');
        this.renderPasscodeGate();
      }
    } catch {
      this.adminKey = savedKey;
      this.render();
      this.fetchData();
      this.startAutoRefresh();
    }
  }

  private handleUnauthorized() {
    sessionStorage.removeItem('vyko_admin_key');
    this.adminKey = '';
    this.stopAutoRefresh();
    this.renderPasscodeGate('Session expired or passcode changed. Please sign in again.');
  }

  private showToast(message: string, isSuccess = true) {
    showToast(message, isSuccess ? 'success' : 'error');
  }

  private async copyTokenToClipboard(token: string, targetBtn?: HTMLElement) {
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(token);
        copied = true;
      }
    } catch {
      copied = false;
    }

    if (!copied) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = token;
        textarea.style.position = 'fixed';
        textarea.style.top = '-9999px';
        textarea.style.left = '-9999px';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        copied = document.execCommand('copy');
        textarea.remove();
      } catch {
        copied = false;
      }
    }

    if (copied) {
      if (targetBtn) {
        const originalHtml = targetBtn.innerHTML;
        targetBtn.innerHTML = '<span style="color: var(--system-green); font-weight: 600;">✓ Copied</span>';
        setTimeout(() => {
          targetBtn.innerHTML = originalHtml;
        }, 1800);
      }
      this.showToast('Session token copied');
    } else {
      this.showToast('Could not copy token', false);
    }
  }

  private formatTimestamp(isoString?: string | null): string {
    if (!isoString) return '—';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return isoString;
      return d.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return isoString;
    }
  }

  private formatRelativeTime(isoString?: string | null): string {
    if (!isoString) return 'Never';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return isoString;
      const diffMs = Date.now() - d.getTime();
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHr = Math.floor(diffMin / 60);
      const diffDay = Math.floor(diffHr / 24);

      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      if (diffHr < 24) return `${diffHr}h ago`;
      if (diffDay < 7) return `${diffDay}d ago`;
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch {
      return isoString || 'Never';
    }
  }

  private formatFullDateTime(isoString?: string | null): string {
    if (!isoString) return '—';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return isoString;
      return d.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    } catch {
      return isoString;
    }
  }

  private getPageNumbers(currentPage: number, totalPages: number): (number | '...')[] {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, '...', totalPages];
    } else if (currentPage >= totalPages - 2) {
      return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    } else {
      return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
    }
  }

  private renderPasscodeGate(initialError: string = '') {
    this.container.innerHTML = `
      <div class="bypass-clean-wrapper">
        <div class="bypass-clean-card">
          <!-- Top Row: Brand & Theme -->
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem;">
            <div style="display: flex; align-items: center; gap: 0.65rem;">
              <img src="/vyko.webp" alt="Vyko" width="34" height="34" style="border-radius: 8px;" />
              <div style="display: flex; align-items: center; gap: 0.4rem;">
                <span style="font-weight: 600; font-size: 1rem; color: var(--text-primary);">Vyko</span>
                <span class="admin-badge">Admin</span>
              </div>
            </div>
            <button type="button" class="btn-icon btn-theme-toggle" id="gate-theme-toggle" title="Toggle theme">
              ${isDarkMode() ? `
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              ` : `
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              `}
            </button>
          </div>

          <!-- Title -->
          <div style="margin-bottom: 1.25rem;">
            <h1 style="font-size: 1.25rem; font-weight: 600; color: var(--text-primary); margin: 0; letter-spacing: -0.01em;">
              Admin Access
            </h1>
            <p style="font-size: 0.825rem; color: var(--text-secondary); margin-top: 0.3rem; margin-bottom: 0;">
              Enter your passcode to manage authenticated users and system security.
            </p>
          </div>

          <!-- Error Alert -->
          <div id="admin-auth-error" class="form-alert form-alert-error" style="${initialError ? 'display: block;' : 'display: none;'} margin-bottom: 1rem;">
            ${initialError || ''}
          </div>

          <!-- Passcode Form -->
          <form id="admin-auth-form" style="display: flex; flex-direction: column; gap: 0.95rem;">
            <div>
              <label for="admin-passcode-input" style="font-size: 0.75rem; font-weight: 500; color: var(--text-secondary); display: block; margin-bottom: 0.35rem;">
                Passcode
              </label>
              <div style="position: relative; width: 100%;">
                <input
                  type="password"
                  id="admin-passcode-input"
                  placeholder="Enter admin passcode"
                  autocomplete="current-password"
                  style="width: 100%; box-sizing: border-box; font-family: 'JetBrains Mono', monospace; font-size: 0.85rem; background: var(--bg-input); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 0.65rem 2.4rem 0.65rem 0.75rem; color: var(--text-primary); outline: none;"
                  required
                  autofocus
                />
                <button
                  type="button"
                  id="btn-toggle-gate-pass"
                  style="position: absolute; right: 0.65rem; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--text-secondary); cursor: pointer; padding: 0.2rem; display: flex; align-items: center;"
                  title="Show/Hide passcode"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
              </div>
            </div>

            <button
              type="submit"
              id="btn-admin-submit"
              class="btn btn-liquid-accept btn-pill"
              style="min-height: 42px; font-size: 0.875rem; font-weight: 600; width: 100%; margin-top: 0.25rem;"
            >
              Sign In
            </button>

            <div style="display: flex; justify-content: center; margin-top: 0.4rem;">
              <button type="button" id="btn-admin-to-home" class="btn-terms-link" style="font-size: 0.8rem; color: var(--text-secondary); text-decoration: none;">
                &larr; Return to portal
              </button>
            </div>
          </form>
        </div>
      </div>
    `;

    const form = this.container.querySelector('#admin-auth-form') as HTMLFormElement;
    const input = this.container.querySelector('#admin-passcode-input') as HTMLInputElement;
    const errorEl = this.container.querySelector('#admin-auth-error') as HTMLElement;
    const submitBtn = this.container.querySelector('#btn-admin-submit') as HTMLButtonElement;
    const togglePassBtn = this.container.querySelector('#btn-toggle-gate-pass') as HTMLButtonElement;

    togglePassBtn?.addEventListener('click', () => {
      if (input.type === 'password') {
        input.type = 'text';
      } else {
        input.type = 'password';
      }
    });

    this.container.querySelector('#gate-theme-toggle')?.addEventListener('click', () => {
      toggleTheme();
      this.renderPasscodeGate();
    });

    this.container.querySelector('#btn-admin-to-home')?.addEventListener('click', (e) => {
      e.preventDefault();
      window.history.pushState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    });

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const code = input.value.trim();
      if (!code) return;

      submitBtn.disabled = true;
      submitBtn.textContent = 'Verifying...';
      errorEl.style.display = 'none';

      try {
        const res = await fetch('/api/admin/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ passcode: code }),
        });

        const data = await res.json().catch(() => ({})) as any;

        if (res.ok && data?.success) {
          this.adminKey = code;
          sessionStorage.setItem('vyko_admin_key', code);
          this.render();
          this.fetchData();
          this.startAutoRefresh();
        } else {
          errorEl.style.display = 'block';
          errorEl.textContent = data?.error || 'Incorrect passcode. Please verify your Cloudflare Pages configuration.';
          input.value = '';
          input.focus();
        }
      } catch (err: any) {
        errorEl.style.display = 'block';
        errorEl.textContent = err?.message || 'Connection error. Please try again.';
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Sign In';
      }
    });
  }

  private async fetchData() {
    this.isLoading = true;
    this.updateLoadingState();

    try {
      const res = await fetch('/api/admin/users', {
        headers: { 'x-admin-key': this.adminKey },
      });

      if (!res.ok) {
        if (res.status === 401) {
          this.handleUnauthorized();
          return;
        }
        throw new Error(`Error: ${res.statusText}`);
      }

      const data = await res.json() as any;
      if (data.success) {
        this.stats = data.stats;
        this.users = data.users || [];
        this.renderStats();
        this.renderContent();
      }
    } catch (err: any) {
      console.error('Error fetching admin data:', err);
    } finally {
      this.isLoading = false;
      this.updateLoadingState();
    }
  }

  private startAutoRefresh() {
    this.stopAutoRefresh();
    this.autoRefreshTimer = window.setInterval(() => {
      this.fetchData();
    }, 20000);
  }

  private stopAutoRefresh() {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    }
  }

  private updateLoadingState() {
    const icon = this.container.querySelector('#admin-refresh-icon');
    if (icon) {
      if (this.isLoading) icon.classList.add('rotating');
      else icon.classList.remove('rotating');
    }
  }

  public render() {
    this.container.innerHTML = `
      <div class="admin-container">
        <!-- Header -->
        <header class="admin-header">
          <div class="admin-header-brand">
            <img src="/vyko.webp" alt="Vyko" width="30" height="30" style="border-radius: 7px; flex-shrink: 0;" />
            <div>
              <div style="display: flex; align-items: center; gap: 0.5rem;">
                <h1 style="font-size: 1.15rem; font-weight: 600; color: var(--text-primary); margin: 0; letter-spacing: -0.01em;">
                  Users & Sessions
                </h1>
                <span class="admin-badge">Admin</span>
              </div>
              <p style="font-size: 0.76rem; color: var(--text-secondary); margin: 0.15rem 0 0 0;">
                Database records, authentication status, and access control
              </p>
            </div>
          </div>

          <div class="admin-header-controls">
            <!-- Navigation Actions Bar -->
            <div class="admin-header-actions">
              <button type="button" class="btn btn-secondary btn-sm" id="btn-admin-desk-link" style="font-size: 0.78rem; padding: 5px 10px;">
                Portal
              </button>

              <button type="button" class="btn btn-secondary btn-sm" id="btn-admin-bypass-link" style="font-size: 0.78rem; padding: 5px 10px;">
                Token Login
              </button>
            </div>

            <div class="admin-header-divider"></div>

            <!-- Settings / Utilities (Theme Toggle & Sign Out) -->
            <div class="admin-header-settings" aria-label="Settings">
              <button type="button" class="btn-icon btn-theme-toggle" id="admin-theme-toggle" title="Toggle theme" aria-label="Toggle theme" style="width: 34px; height: 34px; flex-shrink: 0;">
                ${isDarkMode() ? `
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                ` : `
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                `}
              </button>

              <button type="button" class="btn-icon btn-signout" id="btn-admin-lock" title="Sign out" aria-label="Sign out" style="width: 34px; height: 34px; flex-shrink: 0;">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
            </div>
          </div>
        </header>

        <!-- Stat Tiles -->
        <div id="admin-stats-container" class="admin-stats-grid">
          <!-- Rendered dynamically -->
        </div>

        <!-- Filter & Search Toolbar -->
        <div class="admin-toolbar">
          <div class="admin-search-group">
            <div class="admin-search-box">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                id="admin-search-input"
                class="admin-search-input"
                placeholder="Search by name, ID, or contact..."
                value="${this.searchQuery}"
              />
            </div>

            <button type="button" class="admin-refresh-btn" id="btn-admin-refresh" title="Refresh records" aria-label="Refresh records">
              <svg id="admin-refresh-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
                <path d="M16 21h5v-5"/>
              </svg>
            </button>
          </div>

          <div class="admin-toolbar-right">
            <div id="filter-pills-container" style="display: flex; gap: 0.25rem;">
              <!-- Filter chips -->
            </div>

            <select id="admin-sort-select" style="background: var(--bg-card); border: 1px solid var(--border-subtle); color: var(--text-primary); border-radius: var(--radius-md); font-size: 0.78rem; padding: 0.45rem 0.65rem; outline: none; cursor: pointer;">
              <option value="active" ${this.sortBy === 'active' ? 'selected' : ''}>Recent activity</option>
              <option value="login" ${this.sortBy === 'login' ? 'selected' : ''}>Logged in time</option>
              <option value="handovers" ${this.sortBy === 'handovers' ? 'selected' : ''}>Handovers</option>
              <option value="name" ${this.sortBy === 'name' ? 'selected' : ''}>Name</option>
              <option value="id" ${this.sortBy === 'id' ? 'selected' : ''}>Login ID</option>
            </select>
          </div>
        </div>

        <!-- Content Mount (Table on Desktop, Cards on Mobile) -->
        <div id="admin-content-mount"></div>

        <!-- User Detail Modal Mount -->
        <div id="admin-detail-modal" class="modal-overlay" style="display: none; z-index: 99999;"></div>
      </div>
    `;

    this.renderStats();
    this.renderContent();
    this.attachEvents();
  }

  private renderStats() {
    const container = this.container.querySelector('#admin-stats-container');
    if (!container) return;

    const stats = this.stats || {
      totalUsers: this.users.length,
      activeNow: 0,
      active24h: 0,
      totalAcceptedHandovers: 0,
      blockedUsers: 0,
    };

    container.innerHTML = `
      <div class="admin-stat-tile">
        <div class="stat-label">Total Users</div>
        <div class="stat-value">${stats.totalUsers}</div>
        <div class="stat-sub">Accounts registered</div>
      </div>

      <div class="admin-stat-tile">
        <div class="stat-label" style="display: flex; align-items: center; gap: 0.35rem;">
          <span class="status-dot active"></span>
          Active Now
        </div>
        <div class="stat-value" style="color: #30D158;">${stats.activeNow}</div>
        <div class="stat-sub">Active in last 15m</div>
      </div>

      <div class="admin-stat-tile">
        <div class="stat-label">Active (24h)</div>
        <div class="stat-value">${stats.active24h}</div>
        <div class="stat-sub">Logged in past 24h</div>
      </div>

      <div class="admin-stat-tile">
        <div class="stat-label">Handovers</div>
        <div class="stat-value">${stats.totalAcceptedHandovers}</div>
        <div class="stat-sub">Completed system-wide</div>
      </div>

      <div class="admin-stat-tile">
        <div class="stat-label" style="display: flex; align-items: center; gap: 0.35rem;">
          <span class="status-dot blocked"></span>
          Blocked
        </div>
        <div class="stat-value" style="color: #FF453A;">${stats.blockedUsers}</div>
        <div class="stat-sub">Access revoked</div>
      </div>
    `;
  }

  private getFilteredAndSortedUsers(): AppUserRecord[] {
    const q = this.searchQuery.toLowerCase().trim();
    const now = Date.now();
    const fifteenMinAgo = now - 15 * 60 * 1000;

    let list = this.users.filter((u) => {
      const isBlocked = u.is_blocked === 1;
      const lastActiveVal = u.lastActive || u.last_active_timestamp;
      const lastActiveTime = lastActiveVal ? new Date(lastActiveVal).getTime() : 0;

      if (this.statusFilter === 'blocked' && !isBlocked) return false;
      if (this.statusFilter === 'active' && (isBlocked || lastActiveTime < fifteenMinAgo)) return false;

      if (!q) return true;
      const name = (u.username || '').toLowerCase();
      const loginId = (u.login_id || '').toLowerCase();
      const email = (u.email_or_mob || '').toLowerCase();
      const empId = (u.empid || '').toLowerCase();
      return name.includes(q) || loginId.includes(q) || email.includes(q) || empId.includes(q);
    });

    list.sort((a, b) => {
      if (this.sortBy === 'handovers') {
        return (Number(b.total_accepted_handovers) || 0) - (Number(a.total_accepted_handovers) || 0);
      }
      if (this.sortBy === 'name') {
        return (a.username || '').localeCompare(b.username || '');
      }
      if (this.sortBy === 'id') {
        return (a.login_id || '').localeCompare(b.login_id || '');
      }
      if (this.sortBy === 'login') {
        const tLoginA = (a.loggedInAt || a.login_timestamp) ? new Date(a.loggedInAt || a.login_timestamp || '').getTime() : 0;
        const tLoginB = (b.loggedInAt || b.login_timestamp) ? new Date(b.loggedInAt || b.login_timestamp || '').getTime() : 0;
        return tLoginB - tLoginA;
      }
      const tA = (a.lastActive || a.last_active_timestamp) ? new Date(a.lastActive || a.last_active_timestamp || '').getTime() : 0;
      const tB = (b.lastActive || b.last_active_timestamp) ? new Date(b.lastActive || b.last_active_timestamp || '').getTime() : 0;
      return tB - tA;
    });

    return list;
  }

  private renderContent() {
    const mount = this.container.querySelector('#admin-content-mount');
    const pillsContainer = this.container.querySelector('#filter-pills-container');
    if (!mount) return;

    if (pillsContainer) {
      const now = Date.now();
      const fifteenMinAgo = now - 15 * 60 * 1000;
      const activeCount = this.users.filter((u) => {
        const lastActiveVal = u.lastActive || u.last_active_timestamp;
        return u.is_blocked !== 1 && lastActiveVal && new Date(lastActiveVal).getTime() >= fifteenMinAgo;
      }).length;
      const blockedCount = this.users.filter((u) => u.is_blocked === 1).length;

      pillsContainer.innerHTML = `
        <button type="button" class="btn btn-sm ${this.statusFilter === 'all' ? 'btn-liquid-accept' : 'btn-secondary'}" data-filter="all" style="font-size: 0.75rem; padding: 4px 9px;">
          All (${this.users.length})
        </button>
        <button type="button" class="btn btn-sm ${this.statusFilter === 'active' ? 'btn-liquid-accept' : 'btn-secondary'}" data-filter="active" style="font-size: 0.75rem; padding: 4px 9px;">
          Active (${activeCount})
        </button>
        <button type="button" class="btn btn-sm ${this.statusFilter === 'blocked' ? 'btn-liquid-accept' : 'btn-secondary'}" data-filter="blocked" style="font-size: 0.75rem; padding: 4px 9px;">
          Blocked (${blockedCount})
        </button>
      `;

      pillsContainer.querySelectorAll('[data-filter]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          this.statusFilter = (e.currentTarget as HTMLElement).getAttribute('data-filter') as any;
          this.currentPage = 1;
          this.renderContent();
        });
      });
    }

    const filtered = this.getFilteredAndSortedUsers();

    if (filtered.length === 0) {
      mount.innerHTML = `
        <div style="padding: 2.5rem 1rem; text-align: center; background: var(--bg-card); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); color: var(--text-secondary); font-size: 0.85rem;">
          No matching users found.
        </div>
      `;
      return;
    }

    const totalUsers = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalUsers / this.pageSize));
    if (this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, totalUsers);
    const pageUsers = filtered.slice(startIndex, endIndex);

    const now = Date.now();
    const fifteenMinAgo = now - 15 * 60 * 1000;

    // Desktop Table
    const tableHtml = `
      <div class="admin-desktop-view" style="background: var(--bg-card); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); overflow-x: auto;">
        <table class="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Login ID</th>
              <th>Contact</th>
              <th>Password</th>
              <th>Session Token</th>
              <th>Logged In At</th>
              <th>Last Active</th>
              <th style="text-align: center;">Handovers</th>
              <th style="text-align: center;">Status</th>
              <th style="text-align: right;">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${pageUsers.map((user) => {
              const isBlocked = user.is_blocked === 1;
              const lastActiveVal = user.lastActive || user.last_active_timestamp;
              const lastActiveTime = lastActiveVal ? new Date(lastActiveVal).getTime() : 0;
              const isActiveNow = !isBlocked && lastActiveTime >= fifteenMinAgo;
              const statusClass = isBlocked ? 'blocked' : isActiveNow ? 'active' : 'offline';
              const statusText = isBlocked ? 'Blocked' : isActiveNow ? 'Active' : 'Offline';

              const loggedInVal = user.loggedInAt || user.login_timestamp;
              const isPassRevealed = this.revealedPasswords.has(user.login_id);
              const displayPass = user.password
                ? (isPassRevealed ? user.password : '••••••••')
                : '<span style="color: var(--text-secondary); opacity: 0.5;">—</span>';

              const shortToken = user.session_token
                ? (user.session_token.length > 10 ? user.session_token.slice(0, 10) + '…' : user.session_token)
                : '<span style="color: var(--text-secondary); opacity: 0.5;">—</span>';

              return `
                <tr>
                  <td>
                    <div style="font-weight: 600; color: var(--text-primary); font-size: 0.825rem;">
                      ${user.username || 'User'}
                    </div>
                    ${user.empid ? `<div style="font-size: 0.72rem; color: var(--text-secondary);">ID: ${user.empid}</div>` : ''}
                  </td>

                  <td>
                    <code style="font-size: 0.78rem; color: var(--text-primary); background: rgba(125,125,125,0.08); padding: 2px 5px; border-radius: 4px;">${user.login_id}</code>
                  </td>

                  <td style="color: var(--text-secondary); font-size: 0.78rem;">
                    ${user.email_or_mob || '—'}
                  </td>

                  <td>
                    <div style="display: flex; align-items: center; gap: 0.35rem; font-family: 'JetBrains Mono', monospace; font-size: 0.78rem;">
                      <span>${displayPass}</span>
                      ${user.password ? `
                        <button type="button" class="btn-toggle-pass" data-loginid="${user.login_id}" style="background: none; border: none; color: var(--system-blue); font-size: 0.72rem; cursor: pointer; padding: 0 3px;">
                          ${isPassRevealed ? 'Hide' : 'Show'}
                        </button>
                      ` : ''}
                    </div>
                  </td>

                  <td>
                    <div style="display: flex; align-items: center; gap: 0.35rem; font-family: 'JetBrains Mono', monospace; font-size: 0.75rem;">
                      <span>${shortToken}</span>
                      ${user.session_token ? `
                        <button type="button" class="btn-copy-token" data-token="${encodeURIComponent(user.session_token)}" style="background: none; border: none; color: var(--system-blue); font-size: 0.72rem; cursor: pointer; padding: 0 3px;">
                          Copy
                        </button>
                      ` : ''}
                    </div>
                  </td>

                  <td>
                    <div style="color: var(--text-primary); font-size: 0.78rem; white-space: nowrap;">
                      ${this.formatTimestamp(loggedInVal)}
                    </div>
                    <div style="font-size: 0.68rem; color: var(--text-secondary);">
                      ${this.formatRelativeTime(loggedInVal)}
                    </div>
                  </td>

                  <td>
                    <div style="color: var(--text-primary); font-size: 0.78rem; white-space: nowrap;">
                      ${this.formatRelativeTime(lastActiveVal)}
                    </div>
                    <div style="font-size: 0.68rem; color: var(--text-secondary);">
                      ${this.formatTimestamp(lastActiveVal)}
                    </div>
                  </td>

                  <td style="text-align: center; font-weight: 600; font-size: 0.825rem;">
                    ${user.total_accepted_handovers || 0}
                  </td>

                  <td style="text-align: center;">
                    <span class="status-pill ${statusClass}">
                      <span class="status-dot ${statusClass}"></span>
                      ${statusText}
                    </span>
                  </td>

                  <td style="text-align: right;">
                    <div style="display: inline-flex; align-items: center; gap: 0.35rem;">
                      <button
                        type="button"
                        class="btn btn-sm btn-toggle-block"
                        data-loginid="${user.login_id}"
                        data-blocked="${isBlocked ? '1' : '0'}"
                        style="font-size: 0.74rem; padding: 3px 8px; color: ${isBlocked ? 'var(--system-green)' : 'var(--system-red)'};"
                      >
                        ${isBlocked ? 'Unblock' : 'Block'}
                      </button>

                      <button
                        type="button"
                        class="btn btn-sm btn-detail-user"
                        data-loginid="${user.login_id}"
                        style="font-size: 0.74rem; padding: 3px 8px;"
                      >
                        Details
                      </button>

                      <button
                        type="button"
                        class="btn btn-sm btn-delete-user"
                        data-loginid="${user.login_id}"
                        style="font-size: 0.74rem; padding: 3px 6px; color: var(--text-secondary);"
                        title="Delete record"
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;

    // Mobile View (Clean Cards)
    const mobileHtml = `
      <div class="admin-mobile-view">
        ${pageUsers.map((user) => {
          const isBlocked = user.is_blocked === 1;
          const lastActiveVal = user.lastActive || user.last_active_timestamp;
          const lastActiveTime = lastActiveVal ? new Date(lastActiveVal).getTime() : 0;
          const isActiveNow = !isBlocked && lastActiveTime >= fifteenMinAgo;
          const statusClass = isBlocked ? 'blocked' : isActiveNow ? 'active' : 'offline';
          const statusText = isBlocked ? 'Blocked' : isActiveNow ? 'Active' : 'Offline';
          const loggedInVal = user.loggedInAt || user.login_timestamp;

          return `
            <div class="admin-card">
              <!-- Top Header -->
              <div class="admin-card-top">
                <div class="admin-card-user-info">
                  <div class="admin-card-name">
                    ${user.username || 'User'}
                    ${user.empid ? `<span style="font-weight: 400; font-size: 0.72rem; color: var(--text-secondary); margin-left: 0.25rem;">(ID: ${user.empid})</span>` : ''}
                  </div>
                  <div class="admin-card-loginid">${user.login_id}</div>
                </div>

                <span class="status-pill ${statusClass}">
                  <span class="status-dot ${statusClass}"></span>
                  ${statusText}
                </span>
              </div>

              <!-- Metadata Grid with Timestamps -->
              <div class="admin-card-meta-grid">
                <div class="admin-card-meta-item">
                  <span class="admin-card-meta-label">Contact</span>
                  <span class="admin-card-meta-val">${user.email_or_mob || '—'}</span>
                </div>
                <div class="admin-card-meta-item">
                  <span class="admin-card-meta-label">Handovers</span>
                  <span class="admin-card-meta-val">${user.total_accepted_handovers || 0}</span>
                </div>
                <div class="admin-card-meta-item">
                  <span class="admin-card-meta-label">Logged In</span>
                  <span class="admin-card-meta-val" title="${loggedInVal || ''}">${this.formatTimestamp(loggedInVal)}</span>
                </div>
                <div class="admin-card-meta-item">
                  <span class="admin-card-meta-label">Last Active</span>
                  <span class="admin-card-meta-val" title="${lastActiveVal || ''}">${this.formatRelativeTime(lastActiveVal)}</span>
                </div>
                <div class="admin-card-meta-item" style="grid-column: span 2;">
                  <span class="admin-card-meta-label">Session Token</span>
                  <span class="admin-card-meta-val" style="font-family: 'JetBrains Mono', monospace; font-size: 0.72rem;">
                    ${user.session_token ? (user.session_token.slice(0, 14) + '…') : 'None'}
                  </span>
                </div>
              </div>

              <!-- Card Actions -->
              <div class="admin-card-actions">
                ${user.session_token ? `
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm btn-copy-token"
                    data-token="${encodeURIComponent(user.session_token)}"
                    style="font-size: 0.75rem; padding: 4px 9px;"
                  >
                    Copy Token
                  </button>
                ` : ''}

                <button
                  type="button"
                  class="btn btn-secondary btn-sm btn-detail-user"
                  data-loginid="${user.login_id}"
                  style="font-size: 0.75rem; padding: 4px 9px;"
                >
                  Details
                </button>

                <button
                  type="button"
                  class="btn btn-sm btn-toggle-block"
                  data-loginid="${user.login_id}"
                  data-blocked="${isBlocked ? '1' : '0'}"
                  style="font-size: 0.75rem; padding: 4px 9px; color: ${isBlocked ? 'var(--system-green)' : 'var(--system-red)'};"
                >
                  ${isBlocked ? 'Unblock' : 'Block'}
                </button>

                <button
                  type="button"
                  class="btn btn-sm btn-delete-user"
                  data-loginid="${user.login_id}"
                  style="font-size: 0.75rem; padding: 4px 8px; color: var(--text-secondary);"
                  title="Delete user"
                >
                  ✕
                </button>
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;

    // Redesigned Pagination Controls
    const pageNumbers = this.getPageNumbers(this.currentPage, totalPages);
    const paginationHtml = `
      <div class="admin-pagination-card">
        <div class="pagination-left">
          <span class="pagination-info">
            Showing <strong class="pagination-highlight">${totalUsers === 0 ? 0 : startIndex + 1}–${endIndex}</strong> of <strong class="pagination-highlight">${totalUsers}</strong> users
          </span>
          <div class="pagination-size-wrap">
            <span>Rows:</span>
            <select id="admin-pagesize-select" class="pagination-select">
              <option value="10" ${this.pageSize === 10 ? 'selected' : ''}>10</option>
              <option value="25" ${this.pageSize === 25 ? 'selected' : ''}>25</option>
              <option value="50" ${this.pageSize === 50 ? 'selected' : ''}>50</option>
              <option value="100" ${this.pageSize === 100 ? 'selected' : ''}>100</option>
            </select>
          </div>
        </div>

        <div class="pagination-right">
          <!-- First Page -->
          <button
            type="button"
            class="pagination-btn"
            id="btn-admin-first-page"
            ${this.currentPage <= 1 ? 'disabled' : ''}
            title="First page"
            aria-label="First page"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/>
            </svg>
          </button>

          <!-- Prev Page -->
          <button
            type="button"
            class="pagination-btn"
            id="btn-admin-prev-page"
            ${this.currentPage <= 1 ? 'disabled' : ''}
            title="Previous page"
            aria-label="Previous page"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>

          <!-- Page numbers (desktop) -->
          <div class="pagination-pages-desktop">
            ${pageNumbers.map((p) => {
              if (p === '...') {
                return `<span class="pagination-ellipsis">…</span>`;
              }
              return `
                <button
                  type="button"
                  class="pagination-page-btn ${p === this.currentPage ? 'active' : ''}"
                  data-page="${p}"
                >
                  ${p}
                </button>
              `;
            }).join('')}
          </div>

          <!-- Page indicator (mobile) -->
          <span class="pagination-pages-mobile">
            Page ${this.currentPage} of ${totalPages}
          </span>

          <!-- Next Page -->
          <button
            type="button"
            class="pagination-btn"
            id="btn-admin-next-page"
            ${this.currentPage >= totalPages ? 'disabled' : ''}
            title="Next page"
            aria-label="Next page"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>

          <!-- Last Page -->
          <button
            type="button"
            class="pagination-btn"
            id="btn-admin-last-page"
            ${this.currentPage >= totalPages ? 'disabled' : ''}
            title="Last page"
            aria-label="Last page"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    mount.innerHTML = tableHtml + mobileHtml + paginationHtml;
    this.attachTableEvents();
  }

  private attachEvents() {
    this.container.querySelector('#btn-admin-refresh')?.addEventListener('click', () => {
      this.fetchData();
    });

    this.container.querySelector('#admin-theme-toggle')?.addEventListener('click', () => {
      toggleTheme();
      this.render();
    });

    this.container.querySelector('#btn-admin-bypass-link')?.addEventListener('click', (e) => {
      e.preventDefault();
      window.history.pushState({}, '', '/BypassLogin');
      window.dispatchEvent(new PopStateEvent('popstate'));
    });

    this.container.querySelector('#btn-admin-desk-link')?.addEventListener('click', (e) => {
      e.preventDefault();
      window.history.pushState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    });

    this.container.querySelector('#btn-admin-lock')?.addEventListener('click', () => {
      sessionStorage.removeItem('vyko_admin_key');
      this.adminKey = '';
      this.stopAutoRefresh();
      this.renderPasscodeGate();
    });

    const searchInput = this.container.querySelector('#admin-search-input') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.currentPage = 1;
      this.renderContent();
    });

    const sortSelect = this.container.querySelector('#admin-sort-select') as HTMLSelectElement;
    sortSelect?.addEventListener('change', (e) => {
      this.sortBy = (e.target as HTMLSelectElement).value as any;
      this.currentPage = 1;
      this.renderContent();
    });
  }

  private attachTableEvents() {
    // Reveal password
    this.container.querySelectorAll('.btn-toggle-pass').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const loginId = (e.currentTarget as HTMLElement).getAttribute('data-loginid') || '';
        if (this.revealedPasswords.has(loginId)) this.revealedPasswords.delete(loginId);
        else this.revealedPasswords.add(loginId);
        this.renderContent();
      });
    });

    // Copy token
    this.container.querySelectorAll('.btn-copy-token').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const targetBtn = e.currentTarget as HTMLElement;
        const token = decodeURIComponent(targetBtn.getAttribute('data-token') || '');
        if (token) {
          await this.copyTokenToClipboard(token, targetBtn);
        }
      });
    });

    // Inspect user
    this.container.querySelectorAll('.btn-detail-user').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const loginId = (e.currentTarget as HTMLElement).getAttribute('data-loginid') || '';
        const user = this.users.find((u) => u.login_id === loginId);
        if (user) this.showDetailModal(user);
      });
    });

    // Toggle block
    this.container.querySelectorAll('.btn-toggle-block').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const button = e.currentTarget as HTMLButtonElement;
        const loginId = button.getAttribute('data-loginid') || '';
        const currentlyBlocked = button.getAttribute('data-blocked') === '1';
        const newBlockedState = !currentlyBlocked;

        button.disabled = true;

        try {
          const res = await fetch('/api/admin/toggle-block', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-admin-key': this.adminKey,
            },
            body: JSON.stringify({ loginId, isBlocked: newBlockedState }),
          });

          if (res.ok) {
            const user = this.users.find((u) => u.login_id === loginId);
            if (user) user.is_blocked = newBlockedState ? 1 : 0;
            if (this.stats) {
              if (newBlockedState) this.stats.blockedUsers++;
              else this.stats.blockedUsers = Math.max(0, this.stats.blockedUsers - 1);
            }
            this.renderStats();
            this.renderContent();
            this.showToast(newBlockedState ? `Blocked ${loginId}` : `Unblocked ${loginId}`);
          } else {
            if (res.status === 401) {
              this.handleUnauthorized();
              return;
            }
            this.showToast('Failed to update user block status.', false);
            button.disabled = false;
          }
        } catch (err: any) {
          this.showToast(err?.message || 'Network error', false);
          button.disabled = false;
        }
      });
    });

    // Delete
    this.container.querySelectorAll('.btn-delete-user').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const loginId = (e.currentTarget as HTMLElement).getAttribute('data-loginid') || '';
        if (!confirm(`Permanently delete account "${loginId}" from database?`)) return;

        try {
          const res = await fetch('/api/admin/delete-user', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-admin-key': this.adminKey,
            },
            body: JSON.stringify({ loginId }),
          });

          if (res.ok) {
            this.users = this.users.filter((u) => u.login_id !== loginId);
            if (this.stats) this.stats.totalUsers = Math.max(0, this.stats.totalUsers - 1);
            this.renderStats();
            this.renderContent();
            this.showToast(`Deleted ${loginId}`);
          } else {
            if (res.status === 401) {
              this.handleUnauthorized();
              return;
            }
            this.showToast('Failed to delete user.', false);
          }
        } catch (err: any) {
          this.showToast(err?.message || 'Delete error', false);
        }
      });
    });

    // Redesigned Pagination Controls
    this.container.querySelector('#btn-admin-first-page')?.addEventListener('click', () => {
      if (this.currentPage > 1) {
        this.currentPage = 1;
        this.renderContent();
      }
    });

    this.container.querySelector('#btn-admin-prev-page')?.addEventListener('click', () => {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.renderContent();
      }
    });

    this.container.querySelector('#btn-admin-next-page')?.addEventListener('click', () => {
      const filtered = this.getFilteredAndSortedUsers();
      const totalPages = Math.max(1, Math.ceil(filtered.length / this.pageSize));
      if (this.currentPage < totalPages) {
        this.currentPage++;
        this.renderContent();
      }
    });

    this.container.querySelector('#btn-admin-last-page')?.addEventListener('click', () => {
      const filtered = this.getFilteredAndSortedUsers();
      const totalPages = Math.max(1, Math.ceil(filtered.length / this.pageSize));
      if (this.currentPage < totalPages) {
        this.currentPage = totalPages;
        this.renderContent();
      }
    });

    this.container.querySelectorAll('.pagination-page-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const page = Number((e.currentTarget as HTMLElement).getAttribute('data-page'));
        if (page && page !== this.currentPage) {
          this.currentPage = page;
          this.renderContent();
        }
      });
    });

    const pageSizeSelect = this.container.querySelector('#admin-pagesize-select') as HTMLSelectElement | null;
    pageSizeSelect?.addEventListener('change', (e) => {
      this.pageSize = Number((e.target as HTMLSelectElement).value) || 10;
      this.currentPage = 1;
      this.renderContent();
    });
  }

  private showDetailModal(user: AppUserRecord) {
    const mount = this.container.querySelector('#admin-detail-modal') as HTMLElement;
    if (!mount) return;

    const isBlocked = user.is_blocked === 1;
    const loggedInVal = user.loggedInAt || user.login_timestamp;
    const lastActiveVal = user.lastActive || user.last_active_timestamp;

    mount.innerHTML = `
      <div class="bypass-clean-card admin-modal-card">
        <!-- Header -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.25rem;">
          <div>
            <h3 style="font-size: 1.15rem; font-weight: 600; color: var(--text-primary); margin: 0;">${user.username || 'User'}</h3>
            <code style="font-size: 0.8rem; color: var(--text-secondary);">${user.login_id}</code>
          </div>
          <button type="button" class="btn-icon" id="btn-close-detail" style="width: 28px; height: 28px; font-size: 1rem;">✕</button>
        </div>

        <!-- Details Grid -->
        <div style="display: flex; flex-direction: column; gap: 0.85rem; font-size: 0.8rem;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.55rem 0.75rem; background: rgba(125,125,125,0.06); padding: 0.85rem; border-radius: var(--radius-md);">
            <div>Employee ID: <strong style="color: var(--text-primary);">${user.empid || '—'}</strong></div>
            <div>Contact: <strong style="color: var(--text-primary);">${user.email_or_mob || '—'}</strong></div>
            <div>Handovers: <strong style="color: var(--text-primary);">${user.total_accepted_handovers || 0}</strong></div>
            <div>Status: <strong style="color: ${isBlocked ? '#FF453A' : '#30D158'};">${isBlocked ? 'Blocked' : 'Active'}</strong></div>
            
            <div style="grid-column: span 2; border-top: 1px solid var(--border-hairline); margin: 0.2rem 0; padding-top: 0.4rem;"></div>
            
            <div>
              <div style="font-size: 0.7rem; color: var(--text-secondary);">Logged In At (loggedInAt)</div>
              <strong style="color: var(--text-primary); font-size: 0.78rem;">${this.formatFullDateTime(loggedInVal)}</strong>
              <div style="font-size: 0.68rem; color: var(--text-secondary);">${this.formatRelativeTime(loggedInVal)}</div>
            </div>

            <div>
              <div style="font-size: 0.7rem; color: var(--text-secondary);">Last Active (lastActive)</div>
              <strong style="color: var(--text-primary); font-size: 0.78rem;">${this.formatFullDateTime(lastActiveVal)}</strong>
              <div style="font-size: 0.68rem; color: var(--text-secondary);">${this.formatRelativeTime(lastActiveVal)}</div>
            </div>
          </div>

          ${user.password ? `
            <div>
              <div style="font-size: 0.72rem; color: var(--text-secondary); margin-bottom: 0.25rem;">Password</div>
              <code style="display: block; background: rgba(125,125,125,0.06); padding: 0.55rem; border-radius: var(--radius-md); word-break: break-all; color: var(--text-primary); font-family: 'JetBrains Mono', monospace; font-size: 0.8rem;">${user.password}</code>
            </div>
          ` : ''}

          <div>
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.25rem;">
              <span style="font-size: 0.72rem; color: var(--text-secondary);">Session Token</span>
              ${user.session_token ? `<button type="button" id="btn-copy-modal-token" style="background: none; border: none; font-size: 0.72rem; color: var(--system-blue); cursor: pointer; padding: 0; font-weight: 500;">Copy Token</button>` : ''}
            </div>
            <textarea
              rows="3"
              readonly
              style="width: 100%; box-sizing: border-box; font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; background: var(--bg-input); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 0.55rem; color: var(--text-primary); resize: vertical; line-height: 1.4; outline: none;"
            >${user.session_token || 'No active session token'}</textarea>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 0.5rem; flex-wrap: wrap;">
            <button
              type="button"
              class="btn btn-sm"
              id="btn-modal-toggle-block"
              style="color: ${isBlocked ? 'var(--system-green)' : 'var(--system-red)'}; font-size: 0.8rem; padding: 6px 14px;"
            >
              ${isBlocked ? 'Unblock user' : 'Block user'}
            </button>
            <button type="button" class="btn btn-secondary btn-sm" id="btn-close-detail-btn" style="font-size: 0.8rem; padding: 6px 14px;">
              Close
            </button>
          </div>
        </div>
      </div>
    `;

    mount.style.display = 'flex';

    mount.querySelector('#btn-close-detail')?.addEventListener('click', () => {
      mount.style.display = 'none';
    });
    mount.querySelector('#btn-close-detail-btn')?.addEventListener('click', () => {
      mount.style.display = 'none';
    });

    mount.querySelector('#btn-copy-modal-token')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLElement;
      if (user.session_token) {
        await this.copyTokenToClipboard(user.session_token, btn);
      }
    });

    mount.querySelector('#btn-modal-toggle-block')?.addEventListener('click', async () => {
      const currentlyBlocked = user.is_blocked === 1;
      const newBlockedState = !currentlyBlocked;

      try {
        const res = await fetch('/api/admin/toggle-block', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-key': this.adminKey,
          },
          body: JSON.stringify({ loginId: user.login_id, isBlocked: newBlockedState }),
        });

        if (res.ok) {
          user.is_blocked = newBlockedState ? 1 : 0;
          mount.style.display = 'none';
          this.fetchData();
          this.showToast(newBlockedState ? `Blocked ${user.login_id}` : `Unblocked ${user.login_id}`);
        } else {
          if (res.status === 401) {
            mount.style.display = 'none';
            this.handleUnauthorized();
          }
        }
      } catch (err: any) {
        this.showToast(err?.message || 'Action failed', false);
      }
    });

    mount.addEventListener('click', (e) => {
      if (e.target === mount) mount.style.display = 'none';
    });
  }

  public destroy() {
    this.stopAutoRefresh();
  }
}
